/**
 * Keyboard navigation hook for wizard steps
 *
 * Shortcuts:
 * - Alt + Arrow Left: Previous step
 * - Alt + Arrow Right: Next step
 * - Alt + 1-6: Jump to specific step
 * - Escape: Close any open modal/panel
 * - Ctrl + S: Save configuration
 * - Ctrl + E: Export configuration
 */

import { useEffect, useCallback } from 'react';
import { WizardStep, WIZARD_STEPS, canProceedToStep, DatacenterConfig } from '../types/configurator';

interface KeyboardNavigationOptions {
  currentStep: WizardStep;
  config: DatacenterConfig;
  onStepChange: (step: WizardStep) => void;
  onSave?: () => void;
  onExport?: () => void;
  onEscape?: () => void;
  enabled?: boolean;
}

export function useKeyboardNavigation({
  currentStep,
  config,
  onStepChange,
  onSave,
  onExport,
  onEscape,
  enabled = true,
}: KeyboardNavigationOptions) {
  const currentIndex = WIZARD_STEPS.indexOf(currentStep);

  const goToPreviousStep = useCallback(() => {
    if (currentIndex > 0) {
      onStepChange(WIZARD_STEPS[currentIndex - 1]);
    }
  }, [currentIndex, onStepChange]);

  const goToNextStep = useCallback(() => {
    if (currentIndex < WIZARD_STEPS.length - 1) {
      const nextStep = WIZARD_STEPS[currentIndex + 1];
      if (canProceedToStep(nextStep, config)) {
        onStepChange(nextStep);
      }
    }
  }, [currentIndex, config, onStepChange]);

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < WIZARD_STEPS.length) {
        const targetStep = WIZARD_STEPS[stepIndex];
        if (canProceedToStep(targetStep, config)) {
          onStepChange(targetStep);
        }
      }
    },
    [config, onStepChange]
  );

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        // Allow Escape to still work in inputs
        if (event.key !== 'Escape') {
          return;
        }
      }

      // Alt + Arrow keys for navigation
      if (event.altKey && !event.ctrlKey && !event.metaKey) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goToPreviousStep();
          return;
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          goToNextStep();
          return;
        }

        // Alt + 1-6 for direct step navigation
        const num = parseInt(event.key, 10);
        if (num >= 1 && num <= 6) {
          event.preventDefault();
          goToStep(num - 1);
          return;
        }
      }

      // Ctrl/Cmd + S for save
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        onSave?.();
        return;
      }

      // Ctrl/Cmd + E for export
      if ((event.ctrlKey || event.metaKey) && event.key === 'e') {
        event.preventDefault();
        onExport?.();
        return;
      }

      // Escape to close modals
      if (event.key === 'Escape') {
        onEscape?.();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, goToPreviousStep, goToNextStep, goToStep, onSave, onExport, onEscape]);

  return {
    goToPreviousStep,
    goToNextStep,
    goToStep,
    currentIndex,
    canGoBack: currentIndex > 0,
    canGoForward:
      currentIndex < WIZARD_STEPS.length - 1 &&
      canProceedToStep(WIZARD_STEPS[currentIndex + 1], config),
  };
}

/**
 * Component for displaying keyboard shortcut hints
 */
export const KEYBOARD_SHORTCUTS = [
  { keys: ['Alt', '←'], description: 'Previous step' },
  { keys: ['Alt', '→'], description: 'Next step' },
  { keys: ['Alt', '1-6'], description: 'Jump to step' },
  { keys: ['Ctrl', 'S'], description: 'Save configuration' },
  { keys: ['Ctrl', 'E'], description: 'Export configuration' },
  { keys: ['Esc'], description: 'Close panel' },
] as const;
